<template>
  <div class="checkout-address-form">
    <loading-overlay :isLoading="pageLoading" :delayLoading="200" :loadingText="'Loading...'" />
    <h5 class="my-2">
      <b>{{ title }}</b>
    </h5>
    <div class="form-group row">
      <div class="col-md-12">
        <label for="customer-dropdown">Customer:</label>
        <div class="d-flex customer-dropdown">
          <multiselect
            v-model="selectedDBCustomer"
            :options="customers"
            :clear-on-select="false"
            :placeholder="selectedDBCustomer?.customerNo ? customLabel(selectedDBCustomer) : 'Select a customer'"
            track-by="customerNo"
            label="customerNo"
            :showLabels="false"
            @input="handleCustomerSelect"
            :searchable="true"
            :custom-label="customLabel"
            :class="{
              'is-invalid': errors.customerNo,
              'invalid-dropwdown': errors.customerNo,
            }"
          >
          <template #option="{ option }">
            <p class="mb-1 font-weight-bold">
              {{ option.customerNo }} - {{ option.companyName }}
            </p>
            <div>
              <span>{{ option.address?.City }}, {{ states.find(x => x.AlphaCode === option.address?.State)?.Name }} {{ option.address?.Zip }}</span>
            </div>
          </template>
          </multiselect>
          <b-button-group v-if="selectedDBCustomer !== null">
            <b-button variant="outline-danger" size="sm" title="Unselect Customer" @click="removeCustomer">
              <b-icon icon="x" aria-hidden="true"></b-icon>
            </b-button>
            <b-button class="d-flex align-items-center" variant="outline-primary" size="sm" title="Customer Details" target="_blank" :href="'/customer/' + selectedDBCustomer.customerNo" >
              <b-icon icon="person-fill" aria-hidden="true"></b-icon>
            </b-button>
          </b-button-group>
        </div>
        <div v-if="errors.customerNo" class="invalid-feedback">
          {{ errors.customerNo }}
        </div>
      </div>
    </div>
    <div v-if="selectedDBCustomer && customer.isCreditBlocked === true">
      <div class="bordered-container border-danger text-danger">
        {{ errors.creditBlocked }}
      </div>
    </div>
    <div v-if="selectedDBCustomer">
      <label class="floating-label">Address: </label>
      <div class="bordered-container">
        <div>
          <b>{{ customer.companyName }} - {{ customer.customerNo }}</b>
        </div>
        <div>
          <span>{{ customer.address?.Address1 }} {{ customer.address?.Address2 }}</span>
        </div>
        <div>
          <span>{{ customer.address?.City }}, {{ states.find(x => x.AlphaCode === customer.address?.State)?.Name }} {{ customer.address?.Zip }}</span>
        </div>
        <div>
          <span>{{ countries.find(x => x.AlphaCode === customer.address?.Country)?.Name }}</span>
        </div>
        <div>
          <span>Tel: {{ customer.address?.Phone || 'N/A' }}</span>
        </div>
      </div>
    </div>
    <div v-if="selectedDBCustomer && customer.paymentTerm">
      <label class="floating-label">Payment Term: </label>
      <div class="bordered-container"> {{ customer.paymentTerm }}</div>
    </div>
    <div class="form-group row" v-if="showSalesRepresentative">
      <div class="col-md-12">
        <label for="customer-dropdown">Sales Representative:</label>
        <div class="d-flex">
          <multiselect
            v-model="selectedSalesRep"
            :options="_isSalesRepSite(this.site.Name) ? userSalesReps : salesReps"
            :close-on-select="true"
            :clear-on-select="false"
            :allow-empty="false"
            placeholder="Select a sales representative"
            deselect-label="Selected"
            track-by="Id"
            label="companyName"
            select-label=""
            :searchable="true"
            :custom-label="customLabelForSalesRep"
            :class="{
              'is-invalid': errors.customerNo,
              'invalid-dropwdown': errors.customerNo,
            }"
            :disabled="selectedDBCustomer == null"
          >
          </multiselect>
          <b-button-group v-if="selectedSalesRep !== null">
            <b-button variant="outline-danger" size="sm" title="Unselect Sales Rep" @click="unselectSalesRep">
              <b-icon icon="x" aria-hidden="true"></b-icon>
            </b-button>
            <b-button class="d-flex align-items-center" variant="outline-primary" size="sm" title="Sales Rep Details" target="_blank" :href="'/sales-reps/' + selectedSalesRep.Id" >
              <b-icon icon="person-fill" aria-hidden="true"></b-icon>
            </b-button>
          </b-button-group>
        </div>
        <div v-if="errors.customerNo" class="invalid-feedback">
          {{ errors.customerNo }}
        </div>
      </div>
    </div>
    <div class="form-group row" v-if="showOrderContact && selectedDBCustomer">
      <div class="col-md-12">
        <label for="order-contact">Order Contact Email</label>
        <multiselect
          v-show="!customOrderContact"
          v-model="selectedContact"
          :options="customerContacts"
          :close-on-select="true"
          :clear-on-select="false"
          :allow-empty="true"
          placeholder="Select a contact"
          deselect-label="Selected"
          track-by="UserId"
          label="companyName"
          select-label=""
          :searchable="true"
          :custom-label="customLabelForOrderContact"
        >
        </multiselect>
        <input
          v-show="customOrderContact"
          type="email"
          id="order-contact"
          class="form-control"
          v-model.trim="orderContact"
          autocomplete="off"
        />
        <b-form-checkbox v-model="customOrderContact" v-if="customerContacts.length > 0">
          Enter another order contact email
        </b-form-checkbox>
      </div>
    </div>
    <div class="form-group row" v-if="showPOnumber">
      <div class="col-md-12">
        <label for="po-number">PO Number</label>
        <input
          type="text"
          id="po-number"
          class="form-control"
          v-model.trim="customer.poNumber"
          autocomplete="off"
          :class="{
            'is-invalid': errors?.poNumber,
          }"
          @input="onPOChanged"
          autofocus
          maxlength="20"
          ref="poNumberInput"
        />
        <div v-if="errors.poNumber" class="invalid-feedback">
          {{ errors.poNumber }}
        </div>
      </div>
    </div>
    <div class="form-group" v-show="selectedDBCustomer === null && !isBillTo">
      <label for="street-address">Customer Name</label>
      <input
        type="text"
        id="street-address-line1"
        class="form-control"
        v-model.trim="customer.address.Name"
        :class="{
          'is-invalid': errors.AddressName,
        }"
      />
      <div v-if="errors.AddressName" class="invalid-feedback">
        {{ errors.AddressName }}
      </div>
    </div>
    <template v-if="!selectedDBCustomer && !isBillTo">
      <div class="form-group row">
        <div class="col-md-12">
          <label for="country-dropdown">Country: </label>
          <multiselect
            v-model="selectedCountry"
            deselect-label="Selected"
            :allow-empty="false"
            :options="countries"
            :close-on-select="true"
            :clear-on-select="false"
            placeholder="Select a Country"
            track-by="AlphaCode"
            label="Name"
            :searchable="true"
            @input="handleCountrySelect"
            :class="{
              'is-invalid': errors.Country,
              'invalid-dropwdown': errors.Country,
            }"
            :disabled="selectedDBCustomer != null || isBillTo"
          >
          </multiselect>
          <div v-if="errors.Country" class="invalid-feedback">
            {{ errors.Country }}
          </div>
        </div>
      </div>
      <div class="form-group">
        <label for="street-address">Street Address</label>
        <input
          type="text"
          id="street-address-line1"
          class="form-control"
          v-model.trim="customer.address.Address1"
          :class="{
            'is-invalid': errors.Address1,
          }"
          :disabled="selectedDBCustomer != null || isBillTo"
        />
        <div v-if="errors.Address1" class="invalid-feedback">
          {{ errors.Address1 }}
        </div>
        <input
          type="text"
          id="street-address-line2"
          class="form-control mt-3"
          v-model.trim="customer.address.Address2"
          :disabled="selectedDBCustomer != null || isBillTo"
        />
      </div>
      <div class="form-group row">
        <div class="col-md-6" v-if="customer.address.Country === 'US'">
          <label for="state">State</label>
          <select
            id="state"
            class="form-control"
            v-model.trim="customer.address.State"
            :class="{
              'is-invalid': errors.State,
            }"
            :disabled="errors.Country === '' || selectedDBCustomer != null || isBillTo"
          >
            <option value="">Select Your State</option>
            <option
              v-for="state in states"
              :key="state.AlphaCode"
              :value="state.AlphaCode"
            >
              {{ state.Name }}
            </option>
          </select>
          <div v-if="errors.State" class="invalid-feedback">
            {{ errors.State }}
          </div>
        </div>
        <div class="col-md-6" v-else>
          <label for="street-address">State</label>
          <input
            type="text"
            id="state"
            class="form-control"
            v-model.trim="customer.address.State"
            :class="{
              'is-invalid': errors.State,
            }"
            :disabled="selectedDBCustomer != null || isBillTo"
          />
          <div v-if="errors.State" class="invalid-feedback">
            {{ errors.State }}
          </div>
        </div>
        <div class="col-md-6">
          <label for="City">City</label>
          <input
            type="text"
            id="City"
            class="form-control"
            v-model.trim="customer.address.City"
            :class="{
              'is-invalid': errors.City,
            }"
            :disabled="selectedDBCustomer != null || isBillTo"
          />
          <div v-if="!customer.address.City" class="invalid-feedback">
            {{ errors.City }}
          </div>
        </div>
      </div>
      <div class="form-group row">
        <div class="col-md-6">
          <label for="zip-code">Zip/Postal Code</label>
          <input
            id="zip-code"
            class="form-control"
            v-model.trim="customer.address.Zip"
            :class="{
              'is-invalid': errors.Zip,
            }"
            placeholder=""
            :disabled="selectedDBCustomer != null || isBillTo"
          />
          <div v-if="errors.Zip" class="invalid-feedback">
            {{ errors.Zip }}
          </div>
        </div>
        <div class="col-md-6">
          <label for="Phone">Phone</label>
          <input
            type="tel"
            id="Phone"
            class="form-control"
            v-model.trim="customer.address.Phone"
            :class="{
              'is-invalid': errors.Phone,
            }"
            maxlength="10"
            placeholder="(000) 000-0000"
            @input="formatPhoneNumber"
            :disabled="selectedDBCustomer != null || isBillTo"
          />
          <div v-if="errors.Phone" class="invalid-feedback">
            {{ errors.Phone }}
          </div>
        </div>
      </div>
    </template>
    <div class="form-group row" v-if="showShippingMethod">
      <!--div class="col-md-6">
        <label for="multiplier">Multiplier</label>
        <input
          type="number"
          id="multiplier"
          class="form-control"
          v-model.trim="customer.multiplier"
          disabled
        />
      </div-->
    </div>
    <div class="form-check" v-show="showSameAddressCheckbox">
      <input
        type="checkbox"
        id="same-address"
        class="form-check-input mr-2"
        :value="sameAddress"
        @change="handleChangeCheckbox"
      />
      <label for="same-address" class="form-check-label"
        >My Billing and shipping address are the same</label
      >
    </div>
    <!-- <b-button variant="outline-primary float-right my-2" @click="submit"
      >Save</b-button
    > -->
  </div>
</template>
<script lang="ts">
import { namespace } from 'vuex-class'
import MxSite from '@/mixins/site'
import { Component, Prop, Watch, Mixins } from 'vue-property-decorator'
import { ICountry, ICountryState } from '@/store/common/types'
import { IOrderCustomer } from '@/store/orders/types'
import { Api } from '@/api/Api'
import _ from 'lodash'
import Multiselect from 'vue-multiselect'
import { blankCustomer, blankOrderAddress } from '@/store/orders/constants'
import { debounce } from 'typescript-debounce-decorator'
import { ISalesRepresentative } from '@/store/sales-reps/types'
import LoadingOverlay from '@/components/utility/loading-overlay.vue'

const nsCustomer = namespace('customer')
const nsOrder = namespace('orders')

@Component({
  components: {
    Multiselect,
    LoadingOverlay,
  },
})
export default class OrderCustomerForm extends Mixins(MxSite) {
  @Prop({ type: String, required: true }) title!: string
  @Prop({ type: Boolean, default: false }) sameAddress!: boolean
  @Prop({ type: Boolean, default: false }) showSameAddressCheckbox!: boolean
  @Prop({ type: Boolean, default: false }) showMultiplier!: boolean
  @Prop({ type: Boolean, default: false }) showShippingMethod!: boolean
  @Prop({ type: Boolean, default: false }) showPOnumber!: boolean
  @Prop({ type: Boolean, default: false }) showSalesRepresentative!: boolean
  @Prop({ type: Array, default: () => [] }) customers!: IOrderCustomer[]
  @Prop({ type: Boolean, default: false }) isBillTo!: boolean
  @Prop({ type: Array, default: () => [] }) countries!: ICountry[]
  @Prop({ type: Array, default: () => [] }) states!: ICountryState[]
  @Prop({ type: Boolean, default: false }) isPoDuplicated!: boolean
  @Prop({ type: String, required: false }) shippingMethod!: string
  @Prop({ type: Array, default: () => [] }) userSalesReps!: ISalesRepresentative[]
  @Prop({ type: String, required: false }) defaultSalesRepNumber!: string
  @Prop({ type: Boolean, default: false }) showOrderContact!: boolean

  @nsOrder.Action('setCurrentCustomer')
  private setCurrentCustomer: any

  @nsCustomer.State('activeCustomer')
  private activeCustomer: any

  @nsCustomer.State('customerContacts')
  public customerContacts: any

  private api = new Api()
  public customer: IOrderCustomer = _.cloneDeep(blankCustomer)
  public selectedCustomer = ''
  public selectedDBCustomer: any = null
  public searchKeyword = ''
  public selectedCountry: any = null
  public errors: any = {}
  public validated = false
  public salesReps: ISalesRepresentative[] = []
  public selectedSalesRep: any = null
  public customerNo = ''
  public pageLoading = true
  public customOrderContact = false
  public orderContact = ''
  public selectedContact: any = null

  public async mounted() {
    this.customer.shippingMethod = this.shippingMethod
    if (this._isCustomerAdminSite(this.role ?? '')) {
      this.pageLoading = true
      await this.api.fetchV2({ url: '/sales-rep?includePendingPermissions=false&includeUsers=false&pageSize=10000&page=1' })
        .then((salesRepsList) => {
          this.salesReps = [...salesRepsList.List.map((item: ISalesRepresentative) => { return { Id: item.Id, Name: item.Name } })]
          this.pageLoading = false
        })
    }
    this.$nextTick(() => {
      this.focusOnPOInput()
    })
  }

  public creditAlert = false
  private validateCredit(customer: IOrderCustomer) {
    if (customer.isCreditBlocked === true) {
      this.errors.creditBlocked =
        'Your account has a credit hold which is blocking you from creating an order. Please contact TPI Finance Office at 1-800-251-0382 x 337 for assistance.'
      return false
    }
    return true
  }

  public validate() {
    this.errors = {}
    if (!this.validateCredit(this.customer)) {
      if (!this.creditAlert) {
        this.toastWarning(this.errors.creditBlocked)
        this.creditAlert = true
        setTimeout(() => {
          this.creditAlert = false // Reset flag after a certain period
        }, 5000) // Reset after 5 seconds - prevents spamming of alerts
      }
    }
    if (this.isBillTo) {
      if (!this.customer.customerNo) {
        this.errors.customerNo = 'Customer is required'
      }
      if (!this.customer.poNumber) {
        this.errors.poNumber = 'PO Number is required'
      }
      if (this.isPoDuplicated) {
        this.errors.poNumber = 'PO Number already exist'
      }
    } else {
      if (!this.customer.address.Name) {
        this.errors.AddressName = 'Customer is required'
      }
    }

    if (!this.customer.address.Name) {
      this.errors.Name = 'Name is required'
    }

    if (!this.selectedDBCustomer) {
      if (!this.customer.address.Address1) {
        this.errors.Address1 = 'Address1 is required'
      }

      if (!this.customer.address.State) {
        this.errors.State = 'State is required'
      }

      if (!this.customer.address.City) {
        this.errors.City = 'City is required'
      }

      if (!this.customer.address.Zip) {
        this.errors.Zip = 'Zip is required'
      }

      if (!this.customer.address.Phone) {
        this.errors.Phone = 'Phone is required'
      }

      if (!this.customer.address.Country) {
        this.errors.Country = 'Country is required'
      }
    }

    this.validated = true
    return Object.keys(this.errors).length === 0 ? this.customer : null
  }

  public getSelectedSalesRepNumber() {
    return this.selectedSalesRep?.Id
  }

  public getSelectedOrderContactEmail() {
    if (this.customOrderContact) {
      return this.orderContact ?? ''
    } else {
      return this.selectedContact?.Email ?? ''
    }
  }

  // Watchers
  @Watch('customer.shippingMethod')
  onShippingMethodChange(shippingMethod: string) {
    this.$emit('onShippingMethodChange', shippingMethod)
  }

  @Watch('countries')
  onCountriesChanged(newCountries: ICountry[]) {
    this.selectedCountry = newCountries.find((c) => c.AlphaCode === 'US')
  }

  public formatPhoneNumber() {
    const digitsOnly = this.customer.address.Phone.replace(/\D/g, '')
    this.customer.address.Phone = digitsOnly.replace(
      /^(\d{3})(\d{3})(\d{4})$/,
      '($1) $2-$3',
    )
  }

  public handleChangeCheckbox(event: any) {
    this.$emit('handleShowBillingAddress', event.target.checked)
  }

  public async handleCustomerSelect(customer: IOrderCustomer | null) {
    if (
      customer != null &&
      this.customer.customerNo !== customer.customerNo &&
      this.isBillTo
    ) {
      try {
        this.selectedContact = null
        this.customerNo = customer.customerNo
        this.validateCredit(customer)
        this.pageLoading = true
        await this.$store.dispatch(
          'order-state/fetchCustomerShipTos',
          customer.customerNo,
        )
        await this.$store.dispatch(
          'customer/getCustomer',
          customer.customerNo,
        ).then(async () => {
          if (this.showSalesRepresentative) {
            this.selectDefaultSalesRep()
            this.pageLoading = false
          }
        })
        await this.$store.dispatch(
          'multiplier/fetchCustomerMultipliers',
          customer.customerNo,
        )
        await this.$store.dispatch(
          'customer/getContacts',
          customer.customerNo,
        ).then((contacts: any) => {
          this.customOrderContact = contacts.List.length <= 0
        })
      } catch (error) {
        console.error('Error fetching customers:', error)
      }
    }
    if (customer != null) {
      this.customer = {
        ...blankCustomer,
        ...customer,
        poNumber: this.customer.poNumber,
        address: {
          ...customer?.address,
        },
      }
      this.selectedCountry = this.countries.find(
        (c) => c.AlphaCode === customer.address.Country,
      )
    } else {
      this.customer = { ...blankCustomer }
      this.salesReps = []
      this.selectedSalesRep = null
      this.selectedCountry = this.countries.find((c) => c.AlphaCode === 'US')
      if (this.isBillTo) {
        await this.$store.dispatch('multiplier/clearCustomerMultipliers')
        await this.$store.dispatch('customer/clearActiveCustomer')
      }
    }

    this.focusOnPOInput()
    if (this.isBillTo) {
      this.setCurrentCustomer({
        customerNo: this.customer.customerNo,
        divisionCode: this.customer.divisionCode,
        divisionText: this.customer.divisionText,
      })
    }
    this.$emit('customerSelected', this.customer)
  }

  selectDefaultSalesRep() {
    let salesReps = this.userSalesReps
    if (this._isCustomerAdminSite(this.role ?? '')) {
      salesReps = this.salesReps
    }
    if (salesReps.length > 0) {
      this.selectedSalesRep = salesReps[0]
      if (this.defaultSalesRepNumber) {
        const salesRep = salesReps.filter(s => s.Id === this.defaultSalesRepNumber)
        if (salesRep.length > 0) {
          this.selectedSalesRep = salesRep[0]
        }
      } else {
        if (this.activeCustomer && this.activeCustomer.SalesReps && this.activeCustomer.SalesReps.length > 0) {
          const customerSalesRepNumbers = this.activeCustomer.SalesReps.map(s => s.SalesRepNumber)
          const filteredSalesReps = salesReps.filter(s => customerSalesRepNumbers.includes(s.Id))
          if (filteredSalesReps.length > 0) {
            this.selectedSalesRep = filteredSalesReps[0]
          }
        }
      }
    }
  }

  focusOnPOInput() {
    const el = this.$refs.poNumberInput as HTMLInputElement | undefined
    el?.focus()
  }

  public async removeCustomer() {
    this.selectedDBCustomer = null
    this.customer = { ...blankCustomer }
    this.salesReps = []
    this.selectedSalesRep = null
    this.selectedCountry = this.countries.find((c) => c.AlphaCode === 'US')
    if (this.isBillTo) {
      await this.$store.dispatch('multiplier/clearCustomerMultipliers')
      await this.$store.dispatch('customer/clearActiveCustomer')
    }
  }

  public async unselectSalesRep() {
    this.selectedSalesRep = null
  }

  public customLabel({
    companyName,
    customerNo,
  }: {
    companyName: string
    customerNo: string
  }) {
    return `${companyName} - ${customerNo}`
  }

  public async handleCountrySelect(country: { AlphaCode: any } | null) {
    if (country != null) {
      this.customer = {
        ...blankCustomer,
        ...this.customer,
        address: {
          ...blankOrderAddress,
          ...this.customer.address,
          Country: country.AlphaCode,
          State: '',
        },
      }
    } else {
      this.customer = {
        ...blankCustomer,
        ...this.customer,
        address: {
          ...this.customer.address,
          Country: '',
          State: '',
        },
      }
    }
  }

  @debounce(500)
  @Watch('customer', { deep: true })
  onCustomerChange() {
    if (this.validated) {
      this.validate()
    }
  }

  public customLabelForSalesRep({
    Id,
    Name,
  }: {
    Id: string
    Name: string
  }) {
    return Id ? `${Name} - ${Id}` : Name
  }

  public customLabelForOrderContact({
    Fullname,
    Email,
  }: {
    Fullname: string
    Email: string
  }) {
    return `${Fullname} <${Email}>`
  }

  @debounce(500)
  public onPOChanged() {
    this.customer.poNumber = this.customer.poNumber.replace(/\s/g, '')
    this.$emit('checkDuplicatePO', this.customer.poNumber)
  }

  // Delay instant PO validation to be during next
  // @Watch('isPoDuplicated')
  // PoChanged(isPoDuplicated: boolean) {
  //   this.validatePO(isPoDuplicated)
  // }

  // public validatePO(isPoDuplicated: boolean) {
  //   this.errors = {}
  //   if (this.isBillTo && isPoDuplicated) {
  //     this.errors.poNumber = 'PO Number already exist'
  //   }
  // }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.section1 label {
  font-weight: bold;
}

.section1 .form-check-label {
  font-weight: normal;
}

.invalid-dropwdown {
  border: 1px solid #cd0200;
  border-radius: 5px;
}

.bordered-container {
  margin-bottom:20px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  position: relative;
  padding: 15px;
}

.customer-dropdown{
  position: relative;
}
</style>
